import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reset email");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8" data-testid="forgot-back-home">
          <img src={BRAND_ASSETS.logoHorizontalDark} alt="KhojByte" className="h-8" />
        </Link>
        <div className="kb-card p-7">
          {!submitted ? (
            <>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">FORGOT PASSWORD</div>
              <h1 className="font-serif text-2xl text-kb-ink mt-1.5">Reset your password</h1>
              <p className="text-sm text-kb-ink-muted mt-3">
                Enter the email you signed up with. We'll send you a secure link to set a new password.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="kb-input pl-9"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button type="submit" disabled={submitting} className="kb-btn-primary w-full justify-center disabled:opacity-50" data-testid="forgot-submit-btn">
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link to="/login" className="mt-4 inline-flex items-center gap-1 text-xs text-kb-ink-muted hover:text-kb-purple" data-testid="forgot-back-login">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </Link>
            </>
          ) : (
            <div className="text-center py-2" data-testid="forgot-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-xl text-kb-ink mt-4">Check your inbox</h2>
              <p className="text-sm text-kb-ink-muted mt-2">
                If an account exists for <strong>{email}</strong>, we've emailed a reset link. It's valid for one hour.
              </p>
              <Link to="/login" className="kb-btn-outline mt-6 text-sm justify-center inline-flex">
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
